import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Chip,
	ScrollShadow,
	Spinner,
	Tab,
	Tabs,
} from "@heroui/react";
import { useMemo, useEffect, useState } from "react";
import RankList from "./RankList";
import GuardTab from "./GuardTab";
import BoxTab from "./BoxTab";

function RankBlock({
	loading,
	roomMsgRank = [],
	roomAmountRank = [],
	currentRoom,
	onRoomChange,
	guard = [],
	box = [],
	tab = "msg",
}) {
	const [chartMode, setChartMode] = useState(tab);

	// 动态计算可用 Tab 列表（当 guard 或 box 为空时自动隐藏对应 Tab）
	const availableTabs = useMemo(() => {
		const tabs = [
			{ key: "msg", title: "弹幕" },
			{ key: "money", title: "米" },
		];
		if (guard && guard.length > 0) {
			tabs.push({ key: "guard", title: "大航海" });
		}
		if (box && box.length > 0) {
			tabs.push({ key: "box", title: "盲盒" });
		}
		return tabs;
	}, [guard, box]);

	// 当前选中的 Tab 若在可用列表中不存在，安全回退到 "msg"
	const activeKey = availableTabs.some((t) => t.key === chartMode)
		? chartMode
		: "msg";

	useEffect(() => {
		if (!availableTabs.some((t) => t.key === chartMode)) {
			setChartMode("msg");
		}
	}, [availableTabs, chartMode]);

	const countMap = {
		msg: `${roomMsgRank?.length || 0} 个直播间`,
		money: `${roomAmountRank?.length || 0} 个直播间`,
		guard: `${guard?.length || 0} 条记录`,
		box: `${box?.length || 0} 个直播间`,
	};

	const renderEmpty = (text) => (
		<div className="flex flex-col items-center justify-center h-48 text-xs text-default-400">
			{text}
		</div>
	);

	const renderContent = () => {
		switch (activeKey) {
			case "msg":
				return roomMsgRank.length === 0 ? (
					renderEmpty("暂无常看主播数据")
				) : (
					<RankList
						list={roomMsgRank}
						currentRoom={currentRoom}
						onRoomChange={onRoomChange}
					/>
				);
			case "money":
				return roomAmountRank.length === 0 ? (
					renderEmpty("暂无礼物数据")
				) : (
					<RankList
						list={roomAmountRank}
						currentRoom={currentRoom}
						onRoomChange={onRoomChange}
					/>
				);
			case "guard":
				return !guard || guard.length === 0 ? (
					renderEmpty("暂无大航海记录")
				) : (
					<GuardTab guards={guard} />
				);
			case "box":
				return !box || box.length === 0 ? (
					renderEmpty("暂无盲盒数据")
				) : (
					<BoxTab box={box} />
				);
			default:
				return null;
		}
	};

	return (
		<>
			{loading && (
				<div className="absolute inset-0 flex items-center justify-center z-10 bg-white/70 dark:bg-zinc-950/70 rounded-2xl backdrop-blur-xs">
					<Spinner label="加载常看主播中..." />
				</div>
			)}

			<Card
				shadow="sm"
				className="border-default-100/80 bg-content1 p-3 flex flex-col overflow-hidden transition-[height] duration-300 ease-in-out h-[70vh] sm:h-[88vh]"
			>
				<CardHeader className="p-1 pb-3 flex items-center justify-between border-b border-divider shrink-0">
					<div className="flex items-center gap-2 min-w-0">
						<Chip
							size="sm"
							variant="flat"
							color="default"
							className="text-[11px] h-5 px-1.5 font-bold"
						>
							{countMap[activeKey] || ""}
						</Chip>
					</div>

					<div className="flex items-center gap-1.5 shrink-0">
						{currentRoom && (
							<Button
								size="sm"
								variant="light"
								color="danger"
								className="h-6 text-xs px-2 min-w-0 font-medium"
								onPress={() => onRoomChange("")}
							>
								清除 ✕
							</Button>
						)}
						<Tabs
							selectedKey={activeKey}
							onSelectionChange={(k) => setChartMode(k)}
							classNames={{
								tabList: "p-0.5 ",
								tab: "text-xs font-medium",
							}}
						>
							{availableTabs.map((t) => (
								<Tab key={t.key} title={t.title} />
							))}
						</Tabs>
					</div>
				</CardHeader>

				<CardBody className="p-0 pt-2 overflow-hidden flex-1">
					<ScrollShadow
						hideScrollBar
						className="h-full space-y-1 px-0.5"
					>
						{renderContent()}
					</ScrollShadow>
				</CardBody>
			</Card>
		</>
	);
}

export default RankBlock;
